import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { get } from 'lodash';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { growthBookFeatureFlags } from '@utils/featureFlags';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import { triggerToast } from '@components/base/Notification';
import { LoadingSpinner } from '@components/base/Loader';
import { axiosGetDataWithHeaders, axiosPutData } from '@utils/axiosApi';
import { queryParamUtils } from '@utils/readQueryParamFromURL';
const attendTalviewUrl = {
    url: 'https://attend.talview.com/api/mobile',
    liveSession: 'live-session',
    liveAttendee: 'live-attendee',
};
const EXTERNAL_JOIN_URL_ATTENDEE_STATUS = 2;
// API functions
const getUri = async (code) => {
    if (!code)
        return null;
    try {
        const headers = {
            Authorization: `Bearer ${code}`,
        };
        const liveAttendeeData = await axiosGetDataWithHeaders(`${attendTalviewUrl.url}/${attendTalviewUrl.liveAttendee}`, headers);
        if (!get(liveAttendeeData, 'externalJoinUrl')) {
            return { data: liveAttendeeData };
        }
        const id = get(liveAttendeeData, 'id');
        const putResponse = await axiosPutData(`${attendTalviewUrl.url}/${attendTalviewUrl.liveAttendee}/${id}`, { status: EXTERNAL_JOIN_URL_ATTENDEE_STATUS }, headers);
        return { data: putResponse };
    }
    catch (error) {
        console.error('Error in getUri:', error);
        return null;
    }
};
const getLiveSessionDetails = async (code) => {
    if (!code)
        return null;
    try {
        const headers = {
            Authorization: `Bearer ${code}`,
        };
        return await axiosGetDataWithHeaders(`${attendTalviewUrl.url}/${attendTalviewUrl.liveSession}`, headers);
    }
    catch (error) {
        console.error('Error in getLiveSessionDetails:', error);
        return null;
    }
};
// GrowthBook feature functions
const getGrowthbookFeature = async (gb, orgId) => {
    await gb.setAttributes({
        tenant_id: Number(orgId),
    });
    return gb.getFeatureValue(growthBookFeatureFlags.CANDIDATE_FACE_AUTH_REDIRECTION, false);
};
export const ExternalRedirect = () => {
    const growthBook = useGrowthBook();
    const c = queryParamUtils.get('c');
    useEffect(() => {
        const handleRedirect = async () => {
            if (!c) {
                triggerToast({
                    message: {
                        title: _jsx(FormattedMessage, { ...messages.error, values: { error: messages.noRedirectUrl.defaultMessage } }),
                    },
                    variant: 'danger',
                });
                return;
            }
            try {
                // Get URI data
                const uriResponse = await getUri(c);
                if (!uriResponse) {
                    triggerToast({
                        message: {
                            title: _jsx(FormattedMessage, { ...messages.error, values: { error: messages.noRedirectUrl.defaultMessage } }),
                        },
                        variant: 'danger',
                    });
                    return;
                }
                const uriData = uriResponse.data;
                // Check if candidate face auth redirection is enabled
                const candidateFaceAuthRedirectionEnabled = await getGrowthbookFeature(growthBook, get(uriData, 'organization.id') || '');
                // Get the redirect URL
                let redirectUrl = get(uriData, 'externalJoinUrl') || get(uriData, 'joinUrl') || '';
                // If candidate face auth redirection is enabled and user is a candidate
                if (get(uriData, 'role') === 1 && candidateFaceAuthRedirectionEnabled) {
                    // Get live session details
                    const liveSessionData = await getLiveSessionDetails(c);
                    const sectionName = `${get(liveSessionData, 'section.title', 'Live Interview')} (${get(liveSessionData, 'assessment.external_id', '')}, ${get(liveSessionData, 'section.external_id', '')})`;
                    //todo[rafi]: need to add in candidateAuthDomain in env
                    const candidateAuthDomain = 'https://cx.talview.com';
                    // Construct the redirect URL
                    redirectUrl = `${candidateAuthDomain}/biometric-authentication/face-auth?candidateEmail=${encodeURIComponent(get(uriData, 'email', ''))}&organizationId=${get(uriData, 'organization.id', '')}&redirectUrl=${redirectUrl}&description=${encodeURIComponent(sectionName)}`;
                    redirectUrl += `&live_attendee_token=${get(uriData, 'token', '')}`;
                }
                // Redirect to the URL
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                else {
                    triggerToast({
                        message: {
                            title: _jsx(FormattedMessage, { ...messages.error, values: { error: messages.noRedirectUrl.defaultMessage } }),
                        },
                        variant: 'danger',
                    });
                }
            }
            catch (err) {
                console.error('Error during redirect:', err);
                triggerToast({
                    message: {
                        title: _jsx(FormattedMessage, { ...messages.error, values: { error: String(err) } }),
                    },
                    variant: 'danger',
                });
            }
        };
        handleRedirect();
    }, [c, growthBook]);
    return (_jsx("div", { className: "flex h-screen w-full items-center justify-center", children: _jsx(LoadingSpinner, {}) }));
};
export default ExternalRedirect;
